/* ==========================================================================
   HLTH+ Formula Quiz — assets/formula-quiz.js
   8-step wizard rendered into [data-quiz-stage] inside sections/hlth-formula-quiz.liquid.

   Data sources:
   - <script data-quiz-products>  — real product data (variant IDs, prices,
     selling plans) rendered by Liquid from the section's product pickers.
   - <script data-quiz-copy>      — editable results copy from the schema.
   - window.HLTH_QUIZ_CONFIG      — assets/hlth-quiz-config.js (placeholder
     selling-plan overrides; see README-hlth-glp1.md).

   Klaviyo: uses the already-loaded global `klaviyo` object (Klaviyo Shopify
   app embed). Every call is guarded with `if (window.klaviyo)` so the quiz
   works even if Klaviyo is absent.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-formula-quiz]');
  if (!root) return;

  var stage = root.querySelector('[data-quiz-stage]');
  var progressFill = root.querySelector('[data-quiz-progress]');

  // Flavour picker lives outside the stage so its state survives re-renders;
  // moved into the results cart card when a match is shown, stashed back before
  // the stage is cleared.
  var flavourHolder = root.querySelector('[data-quiz-flavour-holder]');
  var flavourPicker = document.getElementById('hlth-quiz-flavours-picker');
  // Trust template lives in the container, a sibling of the card (like the
  // flavour holder), so query the document — root is the card only.
  var trustTpl = document.querySelector('[data-quiz-trust]');
  function stashFlavourPicker() {
    if (flavourPicker && flavourHolder && flavourPicker.parentNode !== flavourHolder) {
      flavourHolder.appendChild(flavourPicker);
    }
  }

  var productsEl = document.querySelector('[data-quiz-products]');
  var copyEl = document.querySelector('[data-quiz-copy]');
  var PRODUCTS = productsEl ? JSON.parse(productsEl.textContent) : {};
  var COPY = copyEl ? JSON.parse(copyEl.textContent) : {};
  var CONFIG = window.HLTH_QUIZ_CONFIG || {};

  /* ---- Step definitions --------------------------------------------------
     Questions and options are editable in the theme editor (each is a
     "Question" block on the section) and rendered to <script data-quiz-steps>.
     The matching logic below keys off the answer `value`s, so keep those
     stable even when editing labels/copy. `max` (ranked) comes through as a
     number; single/multi ignore it. */

  var stepsEl = document.querySelector('[data-quiz-steps]');
  var STEPS = stepsEl ? JSON.parse(stepsEl.textContent) : [];
  var TOTAL_STEPS = STEPS.length;

  var PRIORITY_LABELS = {};
  var prioritiesStep = STEPS.filter(function (s) { return s.key === 'priorities'; })[0];
  if (prioritiesStep) {
    prioritiesStep.options.forEach(function (o) { PRIORITY_LABELS[o.value] = o.label; });
  }
  /* Results show one benefit pill per ranked priority, so the cap follows the
     step's own "max" setting — raise it in the editor and the pills follow. */
  var PRIORITY_MAX = (prioritiesStep && parseInt(prioritiesStep.max, 10)) || 5;

  /* ---- State ------------------------------------------------------------- */

  var state = { step: 0, answers: {} };
  var quizStarted = false;

  function track(event, props) {
    if (window.klaviyo) window.klaviyo.push(['track', event, props || {}]);
  }

  /* ---- Formula matching (pure) ------------------------------------------- */

  function resolveFormulaVariant(answers) {
    var suffix = answers.ageBracket === '50plus' ? '50' : '';
    return (answers.gender || 'women') + suffix;
  }

  // Friendly names for what the user already takes (for the rationale sentence).
  var STACK_LABELS = {
    multivitamin: 'a multivitamin', protein: 'protein', collagen: 'collagen',
    'fish-oil': 'omega-3s', magnesium: 'magnesium', iron: 'iron',
    b12: 'B12', vitaminD: 'vitamin D', probiotic: 'a probiotic', greens: 'greens'
  };

  // Top-priority → results tagline + the lead benefit pill.
  var PRIORITY_CONTENT = {
    'protect-muscle':  { tagline: 'Protect lean muscle while you lose weight',      tag: 'Muscle support' },
    'energy':          { tagline: 'Steady energy through a lower-calorie phase',    tag: 'Energy from key nutrients' },
    'reduce-fatigue':  { tagline: 'Beat the fatigue of eating less',                tag: 'Fights fatigue' },
    'hair-skin-nails': { tagline: 'Protect hair, skin & nails while you lose',      tag: 'Hair, skin & nails' },
    'digestion':       { tagline: 'Gentle digestion while you lose weight',         tag: 'Digestive comfort' },
    'immune':          { tagline: 'Support your immune health while losing weight', tag: 'Immune support' },
    'sleep':           { tagline: 'Support recovery & sleep while you lose',        tag: 'Sleep & recovery' },
    'hormonal':        { tagline: 'Support hormonal balance while you lose',        tag: 'Hormonal support' },
    'strength':        { tagline: 'Hold onto strength & performance while you lose', tag: 'Strength support' }
  };

  /* One message per option in the "What do you currently take?" step, all
     written in the theme editor. A blank message is dropped, so clearing a
     field removes that line from the results entirely. */
  var GAP_RULES = (COPY.gapMessages || []).filter(function (r) {
    return r && r.missing && r.label && String(r.label).trim();
  }).map(function (r) {
    return { missing: r.missing, label: String(r.label).trim() };
  });

  /* Display-only product name. Per-formula override wins; otherwise the real
     product title with the configured word swapped out ("Bundle" →
     "Daily Nutritional Formula"). Never changes what's sent to the cart. */
  function displayTitle(p, fallback) {
    p = p || {};
    if (p.displayName && String(p.displayName).trim()) return String(p.displayName).trim();
    var title = p.title || fallback || '';
    var find = (COPY.titleFind || '').trim();
    if (title && find) {
      var safe = find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      title = title.replace(new RegExp('\\b' + safe + '\\b', 'gi'), (COPY.titleReplace || '').trim())
                   .replace(/\s{2,}/g, ' ')
                   .trim();
    }
    return title || fallback || '';
  }

  function parseList(raw) {
    if (!raw || !String(raw).trim()) return null;
    var items = String(raw).split('\n').map(function (s) { return s.trim(); }).filter(Boolean);
    return items.length ? items : null;
  }

  /* Gifts as "Name | Value" per line. Falls back to a comma-separated list
     (the old format) so existing settings keep working; value is optional. */
  function parseGifts(raw) {
    var src = String(raw || '').trim();
    if (!src) return [];
    var lines = src.indexOf('\n') !== -1 ? src.split('\n') : src.split(',');
    return lines.map(function (line) {
      var bits = String(line).split('|');
      return { title: bits[0].trim(), value: (bits[1] || '').trim() };
    }).filter(function (g) { return g.title; });
  }

  function listPhrase(items) {
    if (!items.length) return '';
    if (items.length === 1) return items[0];
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }

  function matchFormula(answers) {
    var formulaVariant = resolveFormulaVariant(answers);
    var product = PRODUCTS[formulaVariant] || {};
    var stack = answers.currentStack || [];
    var tookNothing = stack.indexOf('nothing') !== -1;

    var gaps = GAP_RULES.filter(function (rule) {
      return tookNothing || stack.indexOf(rule.missing) === -1;
    }).map(function (rule) { return rule.label; });

    var topPriority = answers.priorities && answers.priorities[0];
    var content = PRIORITY_CONTENT[topPriority] ||
      { tagline: 'Complete daily nutrition while you lose weight', tag: 'Complete nutrition' };

    // Copy resolution order everywhere: per-formula field → shared field → smart default.
    function pick(a, b) {
      if (a && String(a).trim()) return String(a).trim();
      if (b && String(b).trim()) return String(b).trim();
      return null;
    }

    // Tagline — per-formula → shared → per-priority default.
    var tagline = pick(product.tagline, COPY.resultTagline) || content.tagline;

    // Rationale paragraph — per-formula → shared → personalised default.
    var rationale = pick(product.rationale, COPY.resultRationale);
    if (rationale) {
      /* use it */
    } else {
      var covered = stack.filter(function (s) { return STACK_LABELS[s]; })
        .map(function (s) { return STACK_LABELS[s]; });
      var priorityWord = (PRIORITY_LABELS[topPriority] || 'complete nutrition').toLowerCase();
      rationale = 'I’d match you to this formula to help support your ' + priorityWord +
        ' priority during a low-calorie weight-loss phase. ';
      if (covered.length) {
        rationale += 'Your current stack covers ' + listPhrase(covered) +
          ', but it doesn’t provide the broader daily nutrient base that’s harder to meet when appetite is reduced and food intake is lower.';
      } else {
        rationale += 'With little in your current routine, it gives you a complete daily nutrient base that’s harder to meet when appetite is reduced and food intake is lower.';
      }
    }

    // Benefit pills — per-formula list → shared list → one pill per ranked priority.
    var tags = parseList(product.benefitTags) || parseList(COPY.resultBenefitTags);
    if (!tags) {
      // Exactly the priorities they ranked, in their own order. Nothing ranked
      // means no pills — we never pad the row with generic filler.
      tags = (answers.priorities || []).map(function (p) {
        return (PRIORITY_CONTENT[p] && PRIORITY_CONTENT[p].tag) || PRIORITY_LABELS[p];
      }).filter(Boolean);
    }
    tags = tags.filter(function (t, i) { return tags.indexOf(t) === i; }).slice(0, PRIORITY_MAX);

    // Prescription-length step removed — everyone starts on a 4-week cadence.
    var deliveryFrequency = '4-week';

    return {
      formulaVariant: formulaVariant,
      product: product,
      gaps: gaps,
      tagline: tagline,
      rationale: rationale,
      benefitTags: tags,
      deliveryFrequency: deliveryFrequency
    };
  }

  // Resolve a selling plan ID for the matched product + frequency:
  // 1. explicit override in HLTH_QUIZ_CONFIG.SELLING_PLAN_MAP,
  // 2. otherwise match the product's real selling plans by name ("4 week"...).
  function resolveSellingPlan(product, frequency) {
    var map = CONFIG.SELLING_PLAN_MAP || {};
    var override = map[frequency];
    if (override && String(override).indexOf('REPLACE') === -1) return parseInt(override, 10);
    var weeks = parseInt(frequency, 10);
    var re = new RegExp('\\b' + weeks + '\\s*-?\\s*week', 'i');
    var plans = product.sellingPlans || [];
    for (var i = 0; i < plans.length; i++) {
      if (re.test(plans[i].name)) return plans[i].id;
    }
    return plans.length ? plans[0].id : null;
  }

  /* ---- Rendering --------------------------------------------------------- */

  function el(tag, className, html) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (html != null) node.innerHTML = html;
    return node;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function setProgress(step) {
    progressFill.style.width = ((step + 1) / TOTAL_STEPS * 100) + '%';
  }

  /* ---- Gender branching -------------------------------------------------
     Any option can be limited to one gender by adding a `genders` array, e.g.
       { value: 'hormonal', label: 'Hormonal balance', genders: ['women'] }
     Options with no `genders` key show for everyone. The filter reads the
     gender captured in step 1, so branch any step's options this way. */
  function visibleOptions(def) {
    var g = state.answers.gender;
    return def.options.filter(function (opt) {
      return !opt.genders || opt.genders.indexOf(g) !== -1;
    });
  }

  function render() {
    stashFlavourPicker();
    stage.innerHTML = '';
    var def = STEPS[state.step];
    setProgress(state.step);

    if (!quizStarted) {
      quizStarted = true;
      track('Started Formula Quiz');
    }

    var stepText = (COPY.stepLabel || 'Step {step} of {total}')
      .replace('{step}', state.step + 1)
      .replace('{total}', TOTAL_STEPS);
    stage.appendChild(el('p', 'hlth-quiz__step-label', esc(stepText)));
    stage.appendChild(el('h3', 'hlth-quiz__question', esc(def.question)));
    if (def.hint) stage.appendChild(el('p', 'hlth-quiz__hint', esc(def.hint)));

    var isChips = def.type === 'multi' || def.type === 'ranked';
    var list = el('div', 'hlth-quiz__options' + (isChips ? ' hlth-quiz__options--chips' : ''));

    visibleOptions(def).forEach(function (opt) {
      var btn = el('button', 'hlth-quiz__option');
      btn.type = 'button';
      btn.dataset.value = opt.value;
      var body = '<span class="hlth-quiz__option-body"><span>' + esc(opt.label) + '</span>';
      if (opt.helper) body += '<small>' + esc(opt.helper) + '</small>';
      body += '</span>';
      if (def.type === 'single') body += '<span class="hlth-quiz__option-arrow" aria-hidden="true">→</span>';
      btn.innerHTML = body;
      btn.addEventListener('click', function () { handleSelect(def, opt, btn); });
      list.appendChild(btn);
    });
    stage.appendChild(list);

    var nav = el('div', 'hlth-quiz__nav');
    if (state.step > 0) {
      var back = el('button', 'hlth-quiz__back', '← Back');
      back.type = 'button';
      back.addEventListener('click', function () {
        state.step -= 1;
        render();
      });
      nav.appendChild(back);
    }
    if (def.type === 'multi' || def.type === 'ranked') {
      var cont = el('button', 'hlth-btn hlth-btn--light hlth-quiz__continue', 'Continue →');
      cont.type = 'button';
      cont.addEventListener('click', function () { advance(def); });
      nav.appendChild(cont);
    }
    stage.appendChild(nav);

    restoreSelections(def, list);
  }

  function restoreSelections(def, list) {
    var answer = state.answers[def.key];
    if (answer == null) return;
    var buttons = list.querySelectorAll('.hlth-quiz__option');
    buttons.forEach(function (btn) {
      var v = btn.dataset.value;
      if (def.type === 'single' && answer === v) btn.classList.add('is-selected');
      if (def.type === 'multi' && answer.indexOf(v) !== -1) btn.classList.add('is-selected');
      if (def.type === 'ranked') {
        var idx = answer.indexOf(v);
        if (idx !== -1) {
          btn.classList.add('is-selected');
          btn.appendChild(el('span', 'hlth-quiz__rank-badge', String(idx + 1)));
        }
      }
    });
  }

  function handleSelect(def, opt, btn) {
    if (def.type === 'single') {
      state.answers[def.key] = opt.value;
      btn.parentElement.querySelectorAll('.hlth-quiz__option').forEach(function (b) {
        b.classList.toggle('is-selected', b === btn);
      });
      // Auto-advance after a beat so the selection is visible.
      setTimeout(function () { advance(def); }, 220);
      return;
    }

    if (def.type === 'multi') {
      var arr = state.answers[def.key] || (state.answers[def.key] = []);
      if (opt.exclusive) {
        // "Nothing" clears everything else.
        state.answers[def.key] = arr.indexOf(opt.value) === -1 ? [opt.value] : [];
      } else {
        var i = arr.indexOf(opt.value);
        if (i === -1) arr.push(opt.value); else arr.splice(i, 1);
        // Selecting anything real deselects "nothing".
        var ni = arr.indexOf('nothing');
        if (ni !== -1 && arr.length > 1) arr.splice(ni, 1);
      }
      rerenderOptions(def);
      return;
    }

    if (def.type === 'ranked') {
      var ranks = state.answers[def.key] || (state.answers[def.key] = []);
      var idx = ranks.indexOf(opt.value);
      if (idx !== -1) ranks.splice(idx, 1);
      else if (ranks.length < def.max) ranks.push(opt.value);
      rerenderOptions(def);
    }
  }

  function rerenderOptions(def) {
    var list = stage.querySelector('.hlth-quiz__options');
    list.querySelectorAll('.hlth-quiz__option').forEach(function (btn) {
      btn.classList.remove('is-selected');
      var badge = btn.querySelector('.hlth-quiz__rank-badge');
      if (badge) badge.remove();
    });
    restoreSelections(def, list);
  }

  function advance(def) {
    if (def.type === 'single' && state.answers[def.key] == null) return;
    if (state.step < TOTAL_STEPS - 1) {
      state.step += 1;
      render();
    } else {
      finish();
    }
  }

  /* ---- Completion -------------------------------------------------------- */

  function finish() {
    setProgress(TOTAL_STEPS - 1);
    var match = matchFormula(state.answers);
    var a = state.answers;

    track('Completed Formula Quiz', {
      gender: a.gender,
      ageBracket: a.ageBracket,
      journeyStage: a.glp1Tenure,
      activityLevel: a.activityLevel,
      currentStack: a.currentStack || [],
      priorities: a.priorities || [],
      matchedFormula: match.formulaVariant
    });
    if (window.klaviyo) {
      window.klaviyo.push(['identify', {
        journey_stage: a.glp1Tenure,
        activity_level: a.activityLevel,
        current_stack: (a.currentStack || []).join(', '),
        priorities: (a.priorities || []).join(', '),
        matched_formula: match.formulaVariant
      }]);
    }

    stashFlavourPicker();
    stage.innerHTML = '';
    var loading = el('div', 'hlth-quiz__loading');
    loading.appendChild(el('div', 'hlth-quiz__spinner'));
    loading.appendChild(el('p', null, esc(COPY.loadingText || 'Matching your formula…')));
    stage.appendChild(loading);

    setTimeout(function () { renderResult(match); }, 1400);
  }

  function renderResult(match) {
    var p = match.product;
    stashFlavourPicker();
    stage.innerHTML = '';
    var wrap = el('div', 'hlth-quiz__result');

    wrap.appendChild(el('span', 'hlth-quiz__result-eyebrow', esc(COPY.resultEyebrow || 'Your match')));
    wrap.appendChild(el('h3', 'hlth-quiz__result-title', esc(displayTitle(p, 'Your HLTH+ Formula'))));
    wrap.appendChild(el('div', 'hlth-quiz__result-tagline', esc(match.tagline)));
    wrap.appendChild(el('p', 'hlth-quiz__result-rationale', esc(match.rationale)));

    // No pills at all when nothing was ranked — don't leave an empty row.
    if ((match.benefitTags || []).length) {
      var tags = el('div', 'hlth-quiz__result-tags');
      match.benefitTags.forEach(function (t) {
        tags.appendChild(el('span', 'hlth-quiz__result-tag', '<span class="hlth-quiz__tag-dot" aria-hidden="true"></span>' + esc(t)));
      });
      wrap.appendChild(tags);
    }

    if (match.gaps.length) {
      var missing = el('div', 'hlth-quiz__missing');
      missing.appendChild(el('h4', null, '⚠ ' + esc(COPY.missingHeading || 'What your current stack is missing')));
      var ul = el('ul');
      match.gaps.forEach(function (g) { ul.appendChild(el('li', null, esc(g))); });
      missing.appendChild(ul);
      wrap.appendChild(missing);
    }

    // Three product image tiles — same 3-up row the bundle cards use.
    var tiles = (p.images && p.images.length) ? p.images.slice(0, 3) : (p.image ? [p.image] : []);
    if (tiles.length) {
      var hero = el('div', 'hlth-quiz__product');
      var row = el('div', 'hlth-quiz__thumbs');
      tiles.forEach(function (src) {
        var t = document.createElement('img');
        t.src = src; t.alt = ''; t.loading = 'lazy';
        row.appendChild(t);
      });
      hero.appendChild(row);
      var ovEyebrow = (p.overlayEyebrow && p.overlayEyebrow.trim()) || COPY.productOverlayEyebrow || '';
      var ovTitle = (p.overlayTitle && p.overlayTitle.trim()) || COPY.productOverlayTitle || '';
      if (ovEyebrow || ovTitle) {
        var ov = el('div', 'hlth-quiz__product-caption');
        if (ovEyebrow) ov.appendChild(el('span', null, esc(ovEyebrow)));
        if (ovTitle) ov.appendChild(el('strong', null, esc(ovTitle)));
        hero.appendChild(ov);
      }
      wrap.appendChild(hero);
    }

    var cart = el('div', 'hlth-quiz__cart');
    cart.appendChild(el('span', 'hlth-quiz__cart-eyebrow', esc(COPY.cartEyebrow || 'Your matched cart is ready')));
    var row = el('div', 'hlth-quiz__cart-row');
    var nameWrap = el('div');
    nameWrap.appendChild(el('div', 'hlth-quiz__cart-name', esc(displayTitle(p, 'HLTH+ Formula') + (COPY.cartNameSuffix || ' · Subscribe & Save'))));
    nameWrap.appendChild(el('div', 'hlth-quiz__cart-meta', esc(COPY.cartMeta || '28-serve subscription bundle')));
    row.appendChild(nameWrap);
    if (p.price) {
      var price = el('div', 'hlth-quiz__cart-price', esc(p.price));
      if (p.comparePrice) price.appendChild(el('s', null, esc(p.comparePrice)));
      row.appendChild(price);
    }
    cart.appendChild(row);

    // Editable delivery frequency — real selling plans from the matched product.
    // Hide the 2-week cadence; label shows just "4 Weeks" (no "Delivered every").
    var plans = (p.sellingPlans || []).filter(function (plan) {
      return !/\b2\s*-?\s*week/i.test(plan.name);
    });
    match.selectedPlanId = resolveSellingPlan(p, match.deliveryFrequency);
    if (plans.length) {
      cart.appendChild(el('span', 'hlth-quiz__freq-label', esc(COPY.frequencyLabel || 'Delivery Frequency')));
      var freqs = el('div', 'hlth-quiz__freqs');
      plans.forEach(function (plan) {
        var label = plan.name.replace(/^\s*deliver(ed)?\s+every\s+/i, '').replace(/^\s*every\s+/i, '').trim();
        var b = el('button', 'hlth-quiz__freq', esc(label));
        b.type = 'button';
        if (plan.id === match.selectedPlanId) b.classList.add('is-active');
        b.addEventListener('click', function () {
          match.selectedPlanId = plan.id;
          freqs.querySelectorAll('button').forEach(function (x) { x.classList.toggle('is-active', x === b); });
        });
        freqs.appendChild(b);
      });
      cart.appendChild(freqs);
    }

    // Flavour selector for the 28-serve bundle (moved in from its holder).
    if (flavourPicker) {
      cart.appendChild(flavourPicker);
    }

    // Free gifts panel — same card treatment as the bundle selector.
    // Blank setting = no gifts panel at all (no hardcoded fallback).
    var gifts = parseGifts(COPY.resultGifts);
    if (gifts.length) {
      var giftsWrap = el('div', 'hlth-quiz__gifts');
      var giftsLabel = (COPY.resultGiftsLabel || 'Your Free Gifts').trim();
      if (giftsLabel) {
        var lab = el('span', 'hlth-quiz__gifts-label');
        lab.insertAdjacentHTML('afterbegin',
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<rect x="3" y="8" width="18" height="13" rx="2"/><path d="M3 12h18M12 8v13"/>' +
          '<path d="M12 8S10.5 3.5 8 4a2 2 0 0 0 0 4zM12 8s1.5-4.5 4-4a2 2 0 0 1 0 4z"/></svg>');
        lab.appendChild(document.createTextNode(giftsLabel));
        giftsWrap.appendChild(lab);
      }
      var giftsRow = el('div', 'hlth-quiz__gifts-row');
      // Equal columns so the cards span the full width of the card.
      giftsRow.style.gridTemplateColumns = 'repeat(' + gifts.length + ', 1fr)';
      gifts.forEach(function (g) {
        var card = el('div', 'hlth-quiz__gift');
        card.appendChild(el('span', 'hlth-quiz__gift-flag', 'FREE'));
        card.appendChild(el('strong', null, esc(g.title)));
        if (g.value) {
          var val = el('span', null, 'Value ');
          var struck = document.createElement('s');
          struck.textContent = g.value;
          val.appendChild(struck);
          card.appendChild(val);
        }
        giftsRow.appendChild(card);
      });
      giftsWrap.appendChild(giftsRow);
      cart.appendChild(giftsWrap);
    }

    var actions = el('div', 'hlth-quiz__actions');
    var addBtn = el('button', 'hlth-btn hlth-btn--primary hlth-quiz__add', esc(COPY.addToCartLabel || 'Buy Now'));
    addBtn.type = 'button';
    addBtn.addEventListener('click', function () { addMatchToCart(match, addBtn); });
    actions.appendChild(addBtn);
    var retake = el('button', 'hlth-quiz__retake-btn', '↻ ' + esc(COPY.retakeLabel || 'Retake'));
    retake.type = 'button';
    retake.addEventListener('click', function () { state = { step: 0, answers: {} }; render(); });
    actions.appendChild(retake);
    cart.appendChild(actions);

    // Trust row (guarantee + payment methods) cloned in under the buttons.
    if (trustTpl) {
      var trust = trustTpl.cloneNode(true);
      trust.removeAttribute('hidden');
      trust.removeAttribute('data-quiz-trust');
      trust.className = 'hlth-quiz__trust';
      cart.appendChild(trust);
    }

    wrap.appendChild(cart);
    wrap.appendChild(el('p', 'hlth-quiz__disclaimer', esc(COPY.disclaimer || 'This is a personalised guide, not medical advice.')));
    stage.appendChild(wrap);
  }

  function addMatchToCart(match, btn) {
    var variantId = match.product.variantId;
    if (!variantId) {
      alert('This formula is not configured yet — pick its product in the theme editor.');
      return;
    }

    // Flavour allocation (must total the pack size) — mirrors the PDP hero props.
    var flavourProps = null;
    if (flavourPicker && flavourPicker.getSelections) {
      var sel = flavourPicker.getSelections();
      if (!sel.isValid) {
        alert('Please choose exactly ' + sel.target + ' serves across the flavours.');
        return;
      }
      flavourProps = { '_pack_size': String(sel.target), 'Purchase Type': sel.target + '-Pack Subscription', '_bundle_type': sel.bundleKey };
      Object.keys(sel.flavours).forEach(function (f) {
        if (sel.flavours[f] > 0) flavourProps['_dynamic_flavour_' + f] = String(sel.flavours[f]);
      });
    }

    track('Added Matched Product to Cart', {
      matchedFormula: match.formulaVariant,
      deliveryFrequency: match.deliveryFrequency
    });

    var item = { id: variantId, quantity: 1 };
    var planId = resolveSellingPlan(match.product, match.deliveryFrequency);
    if (planId) item.selling_plan = planId;
    if (flavourProps) item.properties = flavourProps;

    btn.disabled = true;
    btn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] })
    })
      .then(function (r) { if (!r.ok) throw new Error('Cart error'); return r.json(); })
      .then(function () { window.location.href = CONFIG.AFTER_ADD_URL || '/checkout'; })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = COPY.addToCartLabel || 'Buy Now';
        alert('Sorry, something went wrong adding this to your cart. Please try again.');
      });
  }

  /* ---- Preselect (formula selector buttons / ?variant= param) ------------ */

  var PRESELECT_MAP = {
    women: { gender: 'women', ageBracket: 'under50' },
    women50: { gender: 'women', ageBracket: '50plus' },
    men: { gender: 'men', ageBracket: 'under50' },
    men50: { gender: 'men', ageBracket: '50plus' }
  };

  function applyPreselect(key) {
    var pre = PRESELECT_MAP[key];
    if (!pre) return;
    state.answers.gender = pre.gender;
    state.answers.ageBracket = pre.ageBracket;
    state.step = 2; // gender + age answered, jump to GLP-1 tenure
    render();
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-quiz-preselect]');
    if (link) applyPreselect(link.dataset.quizPreselect);
  });

  var param = new URLSearchParams(window.location.search).get('variant');
  if (param && PRESELECT_MAP[param]) applyPreselect(param);
  else render();
})();
